export interface StorableArrayFetcherOptions {
  requestUrl: string
  requiresAuth?: boolean
  count?: number
  apiVer?: number
}

export function useStorableArrayFetcher<T> ({
  requestUrl,
  requiresAuth = false,
  count = 10,
  apiVer = 1
}: StorableArrayFetcherOptions) {
  const runtimeConfig = useRuntimeConfig()

  const offset = ref<number>(1) // request page index
  const page = ref<number>(1) // current page
  const _count = ref<number>(count)
  const ver = ref<number>(apiVer)

  const { userAuth } = storeToRefs(useAuthStore())

  const arr = ref<T[]>([]) as Ref<T[]>

  const searchParams = reactive<Record<string, string>>({})
  const query = computed(() => {
    const _query = new URLSearchParams(searchParams)
    return _query.toString()
  })
  const { error, pending, refresh: forceRefresh } = useAsyncData(`${requestUrl}/${offset.value}`, async () => {
    if (requiresAuth && !userAuth.value) {
      return
    }
    await $fetch(`${requestUrl}/${offset.value}/${_count.value}?${query.value}`, {
      method: 'GET',
      baseURL: `${runtimeConfig.public.apiUrl}/esim/v${ver.value}/`,
      credentials: 'include',
      headers: {
        'content-type': 'application/json',
        authorization: userAuth.value ? `Bearer ${userAuth.value}` : ''
      }
    }).then((res:any) => {
      if (!res?.success) {
        throw new Error('Failed to fetch orders')
      }
      if (offset.value === 1 && res.data?.length === 0) {
        arr.value = []
      }
      if (res.data?.length) {
        arr.value.splice((offset.value - 1) * _count.value, _count.value, ...res.data)
        if (offset.value > page.value) {
          page.value++
        }
        if (offset.value < page.value) {
          offset.value++
        }
      }
      return arr.value
    })
  },
  {
    watch: [offset, _count, query, userAuth]
  })
  function next () {
    if (offset.value > page.value) {
      return false
    }
    offset.value++
    return true
  }

  function refresh () {
    if (offset.value === 1) {
      forceRefresh()
      return
    }
    offset.value = 1
  }

  function clear () {
    offset.value = 1
    page.value = 1
    arr.value.length = 0
  }

  return {
    arr,
    error,
    pending,
    next,
    refresh,
    clear,
    searchParams
  }
}
